import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useShop } from "@/store/ShopContext";

const PRESETS = [50, 100, 150, 200, 300, 500];

const DESIGNS = [
  {
    id: "warm-clay",
    name: "Terracota",
    swatch: "bg-primary-200",
    accent: "bg-primary-500",
    image:
      "https://readdy.ai/api/search-image?query=Soft%20editorial%20photograph%20of%20a%20warm%20terracotta%20clay%20colored%20gift%20card%20on%20a%20cream%20linen%20tablecloth%20with%20dried%20wheat%20stems%20and%20a%20small%20ceramic%20dish%2C%20natural%20daylight%2C%20documentary%20product%20photography&width=900&height=560&seq=casaflor-giftcard-clay&orientation=landscape",
  },
  {
    id: "moss-green",
    name: "Musgo",
    swatch: "bg-accent-200",
    accent: "bg-accent-500",
    image:
      "https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20muted%20moss%20green%20handmade%20paper%20gift%20card%20laid%20on%20a%20linen%20background%20with%20pressed%20fern%20leaves%2C%20warm%20natural%20daylight%2C%20cream%20palette%2C%20documentary%20style&width=900&height=560&seq=casaflor-giftcard-moss&orientation=landscape",
  },
  {
    id: "soft-cream",
    name: "Creme",
    swatch: "bg-background-200",
    accent: "bg-foreground-950",
    image:
      "https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photograph%20of%20a%20soft%20cream%20colored%20handmade%20paper%20gift%20card%20on%20a%20pale%20stone%20surface%20with%20a%20single%20dried%20rosebud%2C%20natural%20daylight%2C%20warm%20neutral%20palette%2C%20documentary%20product%20photography&width=900&height=560&seq=casaflor-giftcard-cream&orientation=landscape",
  },
];

export default function GiftCardsPage() {
  const navigate = useNavigate();
  const { addToCart, openCart, showToast } = useShop();

  const [amount, setAmount] = useState<number>(100);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [designId, setDesignId] = useState<string>("warm-clay");
  const [recipientName, setRecipientName] = useState<string>("");
  const [recipientEmail, setRecipientEmail] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const design = DESIGNS.find((d) => d.id === designId) ?? DESIGNS[0];
  const finalAmount = customAmount
    ? Math.max(50, Math.min(2000, Math.round(Number(customAmount) || 0)))
    : amount;

  const handleAddToCart = (goToCheckout: boolean) => {
    setError(null);
    if (!recipientName.trim()) {
      setError("Nome do destinatário obrigatório");
      return;
    }
    if (!recipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      setError("E-mail inválido");
      return;
    }
    if (finalAmount < 50) {
      setError("Valor mínimo R$ 50");
      return;
    }

    addToCart({
      productId: 999990 + Math.floor(Math.random() * 9),
      productName: `Vale-Presente — R$${finalAmount}`,
      image: design.image,
      shopName: "Casa Flor",
      skuId: null,
      skuLabel: design.name,
      options: [design.name],
      unitPrice: finalAmount,
      originalPrice: finalAmount,
      currency: "BRL",
      quantity: 1,
      maxStock: 99,
    });

    showToast("Vale-presente adicionado");

    if (goToCheckout) {
      navigate("/checkout");
    } else {
      openCart();
    }
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-10">
          <div className="max-w-[1100px] mx-auto">
            <nav aria-label="Breadcrumb" className="text-[13px] text-foreground-600 mb-6">
              <ol className="flex items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:underline cursor-pointer">
                    Início
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </li>
                <li className="text-foreground-900">Vale-Presente</li>
              </ol>
            </nav>

            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl mb-10">
              Vale-presente
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              {/* Preview */}
              <div className="lg:col-span-6">
                <div className="relative rounded-2xl overflow-hidden bg-background-50 border border-background-200/70">
                  <div className="aspect-[16/10] relative">
                    <img
                      src={design.image}
                      alt={design.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/45 via-foreground-950/5 to-transparent"></div>
                    <div className="absolute inset-0 p-6 flex flex-col justify-between">
                      <span className="self-start inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-background-50/90 backdrop-blur text-foreground-950 text-[12px] font-medium">
                        Casa Flor
                      </span>
                      <div className="text-background-50">
                        <p className="font-heading text-5xl md:text-6xl leading-none">
                          R${finalAmount}
                        </p>
                        {recipientName && (
                          <p className="text-[13.5px] opacity-90 mt-2">
                            Para {recipientName}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {message && (
                    <div className="px-6 py-4 border-t border-background-200/60">
                      <p className="text-[14px] text-foreground-900 leading-6 italic">
                        &quot;{message}&quot;
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-6 space-y-6">
                {/* Amount */}
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.12em] text-foreground-600 mb-2">
                    Valor
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2">
                    {PRESETS.map((v) => {
                      const active = !customAmount && amount === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            setAmount(v);
                            setCustomAmount("");
                          }}
                          className={`h-11 rounded-md border text-[14px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                            active
                              ? "bg-foreground-950 text-background-50 border-foreground-950"
                              : "bg-background-50 text-foreground-900 border-background-300 hover:border-foreground-900"
                          }`}
                        >
                          R${v}
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-700 text-[14px]">
                      R$
                    </span>
                    <input
                      type="number"
                      min={50}
                      max={2000}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Valor personalizado (R$50–R$2000)"
                      className="w-full h-11 pl-9 pr-3 rounded-md border border-background-300 focus:border-foreground-950 outline-none text-[14px] text-foreground-950 bg-background-50"
                    />
                  </div>
                </div>

                {/* Design */}
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.12em] text-foreground-600 mb-2">
                    Design
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {DESIGNS.map((d) => {
                      const active = designId === d.id;
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setDesignId(d.id)}
                          className={`relative rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${
                            active ? "border-foreground-950" : "border-background-200 hover:border-foreground-700"
                          }`}
                        >
                          <div className="aspect-[4/3] bg-background-200">
                            <img
                              src={d.image}
                              alt={d.name}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="px-2 py-1.5 bg-background-50 text-[12.5px] font-medium text-foreground-950">
                            {d.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Recipient */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-[12px] uppercase tracking-[0.12em] text-foreground-600 mb-1.5">
                      Nome do destinatário
                    </label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      className="w-full h-11 px-3 rounded-md border border-background-300 focus:border-foreground-950 outline-none text-[14px] bg-background-50"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] uppercase tracking-[0.12em] text-foreground-600 mb-1.5">
                      E-mail do destinatário
                    </label>
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="w-full h-11 px-3 rounded-md border border-background-300 focus:border-foreground-950 outline-none text-[14px] bg-background-50"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] uppercase tracking-[0.12em] text-foreground-600 mb-1.5">
                      Mensagem (opcional)
                    </label>
                    <textarea
                      rows={3}
                      maxLength={240}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 focus:border-foreground-950 outline-none text-[14px] bg-background-50 resize-none"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-[13px] text-primary-700">{error}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-2.5">
                  <button
                    type="button"
                    onClick={() => handleAddToCart(true)}
                    className="flex-1 h-12 inline-flex items-center justify-center rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Comprar R${finalAmount}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddToCart(false)}
                    className="flex-1 h-12 inline-flex items-center justify-center rounded-full border border-background-300 hover:border-foreground-900 text-foreground-900 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Adicionar ao carrinho
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}